import React from "react";
import { ChevronRightIcon } from "../icons";

// One line of Review & Create. Tapping it jumps to the step that owns the field;
// the wizard brings the user back here afterwards.
//
// The two breakpoints are genuinely different shapes, per the designs:
//   mobile  — full-width rows on hairlines. The ENTIRE row is tappable and the
//             chevron stays vertically centred against the full row height even
//             when the value wraps to two lines (`items-center` does that).
//   desktop — a two-column grid of label-above-value cells, no hairlines. The
//             label is static; the VALUE plus the chevron is the target, with the
//             chevron attached to the end of the value.
//
// Options:
//   icon     — sits immediately before the value (the dot on Allow Late Joins,
//              the crossed-out eye on Portfolio Visibility).
//   pill     — boxes the value, chevron inside the box. For the one row whose
//              value is an award rather than a setting.
//   fullRow  — takes the whole grid row on desktop instead of one cell, so the
//              value has room to breathe. Content still sits left.
//   tone     — "link" tints the value teal, for the league address.
//   lines    — an array instead of a single value, stacked one per line. Client
//              Rev 1, Screen 4: the Prize summary "wraps weirdly. Make it one on
//              top of the other, right-aligned." Right-aligned is the mobile row;
//              the desktop cell keeps its own left alignment.
//   clamp    — one line with an ellipsis instead of wrapping. For the values that
//              run off the screen once the organiser uses the full character
//              count — the league name, its tagline and the link derived from it.
//   clampLines — the same idea over N lines. Client Rev 2 asked for it on Extra
//              prize: "Protection on Screen 4 as well: show the Extra prize value
//              for a maximum of two lines, then truncate with an ellipsis." One
//              line is too tight for a 60-character prize; unbounded wrapping is
//              what let a pasted wall of text take over the Review card.
const ReviewRow = ({
  label,
  value,
  lines,
  icon,
  pill,
  fullRow,
  tone,
  clamp,
  clampLines,
  onEdit,
  editable = true,
}) => {
  const stacked = Array.isArray(lines) ? lines.filter(Boolean) : null;
  const shown = value || "—";
  const valueColor = tone === "link" ? "text-[#0F4146]" : "text-[#0F172A]";

  // `min-w-0` on the flex box and `truncate` on the text is what actually makes
  // the ellipsis appear — without the former the box grows to fit the string and
  // pushes the row off the screen instead.
  // `alignEnd` is the mobile row, whose value column is right-aligned. It only
  // ever sets justify/text-align — never items-*, which would fight the
  // icon's vertical centring (and Tailwind decides that fight by stylesheet
  // order, not by the order the classes are written here).
  const Value = ({ weight, alignEnd }) => (
    <span
      className={`flex items-center gap-1.5 min-w-0 max-w-full text-sm ${weight} ${valueColor} ${
        alignEnd ? "justify-end text-right" : ""
      }`}
    >
      {icon}
      {stacked ? (
        <span
          className={`flex flex-col min-w-0 ${alignEnd ? "items-end" : "items-start"}`}
        >
          {stacked.map((line) => (
            <span key={line} className="truncate">
              {line}
            </span>
          ))}
        </span>
      ) : (
        <span
          className={`min-w-0 ${clamp ? "truncate" : "break-words"} ${
            clampLines ? "overflow-hidden" : ""
          }`}
          // Written inline rather than with Tailwind's `line-clamp-N`: this
          // build's autoprefixer strips `display:-webkit-box` and
          // `-webkit-box-orient` as outdated prefixes, so the emitted utility is
          // just `-webkit-line-clamp` on its own — which clamps nothing. Inline
          // styles skip PostCSS entirely.
          style={
            clampLines
              ? {
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: clampLines,
                }
              : undefined
          }
        >
          {shown}
        </span>
      )}
    </span>
  );

  const chevron = <ChevronRightIcon className="shrink-0 w-4 h-4 text-[#0F4146]" />;

  return (
    <>
      {/* Mobile: label left, value right, on a hairline. The whole row is the
          tap target, and the chevron stays vertically centred against the full
          row height even when the value wraps to two lines. */}
      <button
        type="button"
        onClick={editable ? onEdit : undefined}
        disabled={!editable}
        className="md:hidden w-full flex items-center gap-4 py-3.5 text-left border-b border-divider last:border-b-0 disabled:cursor-default"
      >
        <span className="shrink-0 font-montserrat font-bold text-sm text-[#1A1A1A]">
          {label}
        </span>
        <span className="flex-1 min-w-0 text-right">
          <Value weight="font-medium" alignEnd />
        </span>
        {editable && chevron}
      </button>

      {/* Desktop: one cell of the grid — or the whole row */}
      <div className={`hidden md:block min-w-0 ${fullRow ? "md:col-span-2" : ""}`}>
        <span className="block text-xs text-secondary">{label}</span>
        {editable ? (
          <button
            type="button"
            onClick={onEdit}
            className={`mt-1 inline-flex items-center gap-2 text-left max-w-full ${
              pill
                ? "rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 hover:border-[#0F4146] transition-colors"
                : "items-start gap-1 hover:underline decoration-[#0F4146]/40"
            }`}
          >
            <Value weight="font-semibold" />
            {chevron}
          </button>
        ) : (
          <span className="mt-1 block">
            <Value weight="font-semibold" />
          </span>
        )}
      </div>
    </>
  );
};

export default ReviewRow;
